import React, {
  useContext, useEffect, useMemo, useState
} from 'react';
import proptypes from 'prop-types';
const { string, func, arrayOf, shape, number } = proptypes;
import {
  DrawerBody,
  DrawerFooter,
  Typography,
  DrawerHeader,
  LoadingButton
} from '@one-thd/sui-atomic-components';
import { useStore } from '@thd-nucleus/experience-context';
import {
  params, useDataModel, extend, QueryProvider,
  arrayOf as arrayOfType,
  string as stringType,
  number as numberType,
  bool as boolType,
  shape as shapeType, hoist
} from '@thd-nucleus/data-sources';
import { NOT_FETCHED, OPERATION_ADD_ITEMS, SCREENS, TRACK } from '../../AddToConstants';
import { AddToContext } from '../../AddToContext';
import { MiniListsSummary } from './mini-list-summary/MiniListsSummary';
import { MiniProductList } from './mini-product-list/MiniProductList';
import { useUpdateList, updateListModel } from '../../hooks/useLists';
import { AbandonSelectionWarning } from './AbandonSelectionWarning';
import { triggerAnalytics } from '../../AddToHelpers';
import { useListMetrics } from '../../hooks/useListMetrics';

import { ProductsPropsFetcher } from './ProductsPropsFetcher';
import { consolidateSelectedItems, updateSelectedListItemsProperty } from '../../../utils/copyLists.utils';

const HoistedProductsPropsFetcher = hoist(ProductsPropsFetcher, MiniListsSummary, MiniProductList);

const PASSED_IN_KEY = '__passed_in__';

export const CopyList = ({ listId, onClose, onMaterialsAdded, onUpdateProjects = null, initialProducts = [] }) => {
  const [selectedList, setSelectedList] = useState(null);
  const [selectedListItems, setSelectedListItems] = useState(() => {
    if (initialProducts?.length > 0) {
      const seeded = {
        [PASSED_IN_KEY]: initialProducts.map((product) => ({
          itemId: product.itemId,
          quantity: product.quantity || 1,
          basePrice: onUpdateProjects ? undefined : NOT_FETCHED,
          sku: onUpdateProjects ? (product.sku || NOT_FETCHED) : (product.sku || undefined),
        })),
      };
      return seeded;
    }
    return {};
  });
  const [page, setPage] = useState(1);
  const {
    setScreen,
    screen,
    setSuccessMessage,
    abandonWarning,
    setAbandonWarning,
    canShowWarning,
    setCanShowWarning
  } = useContext(AddToContext);
  const store = useStore({ varnish: true, online: false });
  const { membershipInformation } = store;

  // Use the useListMetrics hook for accurate list totals after copy operation
  const selectedItemsForMetrics = useMemo(() => Object.values(selectedListItems).flat().map((item) => ({
    itemId: item.itemId,
    quantity: item.quantity,
    price: item.basePrice
  }))
  , [selectedListItems]);

  // Get list data for metrics - we need to provide list data for existing list
  const listDataForMetrics = useMemo(() => ({
    id: listId,
    storeId: store.storeId
  }), [listId, store.storeId]);

  const { myListInformation, isLoading: isMetricsLoading } = useListMetrics(
    selectedItemsForMetrics,
    listDataForMetrics,
    { skip: !!onUpdateProjects }
  );

  const {
    data, error, loading: listsLoading, refetch, client
  } = useDataModel('lists', {
    variables: {
      page: page || 1,
      pageSize: 12
    },
    context: { withAuth: true }
  });

  const { lists } = data || {};
  const pagination = lists?.pagination || {};
  const { totalResults } = pagination;

  const { updateList, updateListResponse } = useUpdateList({
    id: listId,
    operationName: OPERATION_ADD_ITEMS,
  });

  const { data: addToListData, loading } = updateListResponse || {};

  const totalItemsSelected = useMemo(() => {
    const total = Object.values(selectedListItems).reduce((acc, items) => acc + items.length, 0);
    return total;
  }, [selectedListItems]);

  useEffect(() => {
    return () => {
      setCanShowWarning(false);
    };
  }, []);

  // 10.2 Copy From Lists Overlay - Track when overlay is shown
  useEffect(() => {
    triggerAnalytics(TRACK.LIST_BUILDER_OVERLAY, {
      overlayType: 'copy from lists'
    });
  }, []);

  const showButtonLoading = useMemo(() => {
    if (loading) return true;
    const selectedProductsToAdd = Object.values(selectedListItems).flat();
    if (onUpdateProjects) {
      // In project mode, pricing/metrics are not used; ignore isMetricsLoading
      return selectedProductsToAdd.some((item) => item.sku === NOT_FETCHED);
    }
    if (isMetricsLoading) return true;
    return selectedProductsToAdd.some((item) => item.basePrice === NOT_FETCHED);
  }, [loading, isMetricsLoading, selectedListItems, onUpdateProjects]);

  useEffect(() => {
    const shouldShowWarning = Boolean(totalItemsSelected);
    if (canShowWarning !== shouldShowWarning) {
      setCanShowWarning(shouldShowWarning);
    }
  }, [totalItemsSelected]);

  const prepareAddToListItems = (addBasePrice = false) => {
    const consolidatedItems = consolidateSelectedItems(selectedListItems);

    if (consolidatedItems.length === 0) {
      return [];
    }

    return consolidatedItems.map((item) => {
      if (addBasePrice) {
        // Removing sku for lists
        const { sku, ...restItem } = item;
        return restItem;
      }
      return {
        itemId: item.itemId,
        quantity: item.quantity,
      };
    });
  };

  const prepareAddToProjectItems = () => {
    const consolidatedItems = consolidateSelectedItems(selectedListItems);

    if (consolidatedItems.length === 0) {
      return [];
    }

    return consolidatedItems.map((item) => {
      const { basePrice, ...restItem } = item;
      return {
        ...restItem,
        itemType: 'catalog'
      };
    });
  };

  useEffect(() => {
    if (addToListData) {
      // Calculate analytics data before submitting using itemsWithPricing instead of selectedListItems
      let entireListCount = 0;
      let partialItemCount = 0;

      // Calculate statistics for each list based on itemsWithPricing
      Object.entries(selectedListItems).forEach(([listIdSelectedItems, items]) => {
        if (listIdSelectedItems === PASSED_IN_KEY) return;
        if (items.length > 0) {
          // Find the current list to check if entire list is selected
          const currentList = lists?.results?.find((list) => list.id === listIdSelectedItems);
          if (currentList && currentList.itemCount === items.length) {
            entireListCount += 1;
          } else {
            partialItemCount += 1;
          }
        }
      });

      // Build location string according to specification
      const locationParts = [];
      if (entireListCount > 0) {
        locationParts.push(`entire>${entireListCount}`);
      }
      if (partialItemCount > 0) {
        locationParts.push(`partial>${partialItemCount}`);
      }
      const location = `existing list: copy list: ${locationParts.join(', ')}`;

      // Format items for analytics using itemsWithPricing which has pricing data
      const itemsWithPrice = prepareAddToListItems(true);
      const items = itemsWithPrice.map((item) => ({
        quantity: item.quantity,
        sku: item.itemId,
        price: item.basePrice,
      }));
      // 6.13.1 Add to List action
      triggerAnalytics(TRACK.ADD_TO_LIST_ADD_ACTION, {
        location,
        items,
        isBulkAction: false,
        // Add myList data for 6.13.1 requirements with accurate totals
        listTotal: myListInformation.listTotal,
        uniqueSkuCount: myListInformation.uniqueSkuCount,
        quantityTotal: myListInformation.quantityTotal
      });

      // Complete the copy operation
      setScreen(SCREENS.DEFAULT);
      const productsAdded = Object.values(selectedListItems).flat();
      setSuccessMessage(`${productsAdded?.length} items successfully added to list`);
      setSelectedListItems({});
      setSelectedList(null);
      onMaterialsAdded();
    }
  }, [addToListData, myListInformation, lists]);

  const onListClick = (currentList) => {
    if (currentList) {
      // 7.5 Mini List Details Overlay
      triggerAnalytics(TRACK.ADD_TO_LIST_MINI_LIST_DETAILS_OVERLAY);
      const { id, name, itemCount } = currentList;
      setSelectedList({ listId: id, listName: name, totalItems: itemCount });
      setScreen(SCREENS.COPY_LIST_PRODUCTS);
    }
  };

  const onBackPress = (__event, overrideWarning = false) => {
    if (screen === SCREENS.COPY_LIST_PRODUCTS) {
      setScreen(SCREENS.COPY_LIST_SUMMARY);
      setSelectedList(null);
      // 7.6 Mini List Details Overlay: Back button
      triggerAnalytics(TRACK.ADD_TO_LIST_MINI_LIST_DETAILS_BACK_PRESS);
    } else {
      const showWarning = canShowWarning && !overrideWarning;
      if (showWarning) {
        setAbandonWarning({ show: true, action: 'BACK' });
      } else {
        setScreen(SCREENS.DEFAULT);
      }
    }
  };

  const onItemSelectionChange = (item) => {
    const {
      listId: currentListId, itemId, quantity, basePrice, sku
    } = item;
    if (loading) return false;
    const currentListItems = selectedListItems[currentListId] || [];
    const isThisItemSelected = currentListItems.some((itemSelected) => itemSelected.itemId === itemId);
    if (isThisItemSelected) {
      setSelectedListItems((prevItems) => ({
        ...prevItems,
        [currentListId]: currentListItems.filter((itemFiltered) => itemFiltered.itemId !== itemId)
      }));
    } else {
      setSelectedListItems((prevItems) => ({
        ...prevItems,
        [currentListId]: [...currentListItems, { itemId, quantity, basePrice, sku }]
      }));
    }
    return true;
  };

  const onListSelectAll = (currentListId, items) => {
    if (loading) return false;
    const currentListItems = selectedListItems[currentListId] || [];
    if (currentListItems.length === items.length) {
      // If all items are already selected, deselect all
      setSelectedListItems((prevItems) => ({
        ...prevItems,
        [currentListId]: []
      }));
    } else {
      if (onUpdateProjects) {
        setSelectedListItems((prevItems) => ({
          ...prevItems,
          [currentListId]: items.map((item) => ({
            itemId: item.itemId, quantity: item.quantity, sku: item.sku
          }))
        }));
        return true;
      }

      setSelectedListItems((prevItems) => ({
        ...prevItems,
        [currentListId]: items.map((item) => ({
          itemId: item.itemId, quantity: item.quantity, basePrice: item.basePrice
        }))
      }));
    }

    return true;
  };

  const getCopyLocationString = () => {
    let entireListCount = 0;
    let partialItemCount = 0;

    Object.entries(selectedListItems).forEach(([listIdSelectedItems, items]) => {
      if (listIdSelectedItems === PASSED_IN_KEY) return;
      if (items.length > 0) {
        const currentList = lists?.results?.find((list) => list.id === listIdSelectedItems);
        if (currentList && currentList.itemCount === items.length) {
          entireListCount += 1;
        } else {
          partialItemCount += 1;
        }
      }
    });

    const locationParts = [];
    if (entireListCount > 0) {
      locationParts.push(`entire>${entireListCount}`);
    }
    if (partialItemCount > 0) {
      locationParts.push(`partial>${partialItemCount}`);
    }
    return locationParts.length > 0 ? `copy list: ${locationParts.join(', ')}` : '';
  };

  const handleSubmit = async () => {
    if (onUpdateProjects) {
      const copyLocation = getCopyLocationString();
      if (initialProducts?.length > 0) {
        const productsToAddProjects = prepareAddToProjectItems();
        await onUpdateProjects({ productsGotten: productsToAddProjects, preview: true, copyLocation });
        return;
      }
      const productsToAddProjects = prepareAddToProjectItems();
      if (productsToAddProjects?.length > 0) {
        await onUpdateProjects({ productsGotten: productsToAddProjects, copyLocation });
        return;
      }
    }

    const productsToAdd = prepareAddToListItems();
    if (productsToAdd?.length > 0) {
      await updateList({
        variables: {
          products: productsToAdd
        }
      });
    }
  };

  const onDrawerClose = (__event, overrideWarning = false) => {
    const showWarning = canShowWarning && !overrideWarning;
    if (showWarning) {
      setAbandonWarning({ show: true, action: 'CLOSE' });
    } else {
      onClose();
    }
  };

  const updateBasePrice = (itemBasePriceMap) => {
    updateSelectedListItemsProperty(setSelectedListItems, itemBasePriceMap, 'basePrice', NOT_FETCHED)();
  };

  const updateSku = (itemSkuMap) => {
    updateSelectedListItemsProperty(setSelectedListItems, itemSkuMap, 'sku', NOT_FETCHED)();
  };

  return (
    <>
      <DrawerHeader
        data-testid="add-to-drawer-header"
        title={`Copy from ${selectedList?.listName || 'Your Lists'}`}
        onClose={onDrawerClose}
        onBack={onBackPress}
      />
      <DrawerBody>
        <div className="sui-pb-4">
          <Typography>
            Use your existing lists to jumpstart this one. Simply copy entire lists or click the
            arrow to select specific items.
          </Typography>
        </div>
        <QueryProvider
          cacheKey="copy-list"
          defaultVariables={{
            isBrandPricingPolicyCompliant: true,
            loyaltyMembershipInput: membershipInformation?.data?.loyaltyMembership
          }}
        >
          <HoistedProductsPropsFetcher
            selectedListItems={selectedListItems}
            updateBasePrice={updateBasePrice}
            updateSku={updateSku}
            isProjects={Boolean(onUpdateProjects)}
          >
            {
              screen === SCREENS.COPY_LIST_SUMMARY && (
                <MiniListsSummary
                  onListClick={onListClick}
                  selectedListItems={selectedListItems}
                  onItemSelectAll={onListSelectAll}
                  currentListId={listId}
                  lists={lists}
                  totalResults={totalResults}
                  loading={listsLoading}
                  setPage={setPage}
                  isProjects={Boolean(onUpdateProjects)}
                />
              )
            }
            {
              screen === SCREENS.COPY_LIST_PRODUCTS && (
                <MiniProductList
                  listId={selectedList?.listId}
                  totalItems={selectedList?.totalItems}
                  onItemSelect={onItemSelectionChange}
                  onItemSelectAll={onListSelectAll}
                  selectedItems={selectedListItems[selectedList?.listId] || []}
                  totalItemsSelected={totalItemsSelected}
                />
              )
            }
          </HoistedProductsPropsFetcher>
        </QueryProvider>

        <AbandonSelectionWarning
          open={abandonWarning?.show}
          handleClose={() => setAbandonWarning({ show: false, action: '' })}
          onConfirm={(__event) => {
            if (abandonWarning?.action === 'CLOSE') {
              onDrawerClose(__event, true);
            } else if (abandonWarning?.action === 'BACK') {
              onBackPress(__event, true);
            }
            setAbandonWarning({ show: false, action: '' });
          }}
        />
      </DrawerBody>
      <DrawerFooter>
        <LoadingButton
          data-testid="copy-list-add-button"
          variant="primary"
          fullWidth
          onClick={handleSubmit}
          type="submit"
          loading={showButtonLoading}
        >
          {onUpdateProjects && initialProducts?.length > 0 ? 'Copy' : 'Add'} {totalItemsSelected} Items
        </LoadingButton>
      </DrawerFooter>
    </>
  );
};

CopyList.displayName = 'CopyList';
CopyList.propTypes = {
  listId: string.isRequired,
  onClose: func.isRequired,
  onMaterialsAdded: func.isRequired,
  onUpdateProjects: func,
  initialProducts: arrayOf(shape({
    itemId: string.isRequired,
    quantity: number,
    sku: string,
  })),
};
CopyList.defaultProps = {
  onUpdateProjects: null,
  initialProducts: [],
};
CopyList.dataModel = extend({
  lists: params({ page: numberType(), pageSize: numberType() }).shape({
    pagination: shapeType({
      pageSize: numberType(),
      totalResults: numberType(),
      totalPages: numberType(),
      hasMore: boolType()
    }),
    results: arrayOfType({
      id: stringType(),
      name: stringType(),
      url: stringType(),
      itemCount: numberType(),
      listAccessType: stringType(),
      images: arrayOfType(shapeType({
        url: stringType()
      }))
    })
  })
}, updateListModel);
